import { motion } from "framer-motion";
import { Search, Sparkles, ExternalLink, Star, MapPin, Phone, Clock, CheckCircle2, Shield, MessageSquare } from "lucide-react";
import { useState, useEffect } from "react";

const AISearchMockup = () => {
  const [typedText, setTypedText] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const fullText = "best plumber near me for emergency";

  useEffect(() => {
    let index = 0;
    const typingInterval = setInterval(() => {
      if (index <= fullText.length) {
        setTypedText(fullText.slice(0, index));
        index++;
      } else {
        clearInterval(typingInterval);
        setTimeout(() => setShowResults(true), 400);
        setTimeout(() => setShowDetails(true), 800);
      }
    }, 60);

    return () => clearInterval(typingInterval);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.3, duration: 0.5 }}
      className="relative w-full max-w-2xl mx-auto"
    >
      {/* Glow effect */}
      <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-accent/20 rounded-3xl blur-2xl" />
      
      {/* Main container */}
      <div className="relative glass rounded-2xl overflow-hidden border border-border/50">
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border/50 bg-secondary/30">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-destructive/60" />
            <div className="w-3 h-3 rounded-full bg-accent/60" />
            <div className="w-3 h-3 rounded-full bg-primary/60" />
          </div>
          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Sparkles className="h-3 w-3 text-accent" />
              <span>ChatGPT</span>
            </div>
          </div>
          <div className="w-16" />
        </div>

        {/* Chat Content */}
        <div className="p-5 space-y-4">
          {/* User Message */}
          <div className="flex justify-end">
            <div className="bg-primary/20 rounded-2xl rounded-br-sm px-4 py-3 max-w-[80%]">
              <p className="text-sm font-medium">
                {typedText}
                {!showResults && <span className="animate-blink text-primary">|</span>}
              </p>
            </div>
          </div>

          {/* AI Response */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: showResults ? 1 : 0, y: showResults ? 0 : 20 }}
            transition={{ duration: 0.4 }}
            className="space-y-3"
          >
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
                <Sparkles className="h-4 w-4 text-accent" />
              </div>
              <div className="space-y-3 flex-1">
                <p className="text-sm text-muted-foreground">
                  I found several highly-rated emergency plumbers in your area. Here's my top recommendation:
                </p>

                {/* Sponsored Ad Result - Premium Card */}
                <motion.div 
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: showDetails ? 1 : 0, scale: showDetails ? 1 : 0.98 }}
                  className="relative"
                >
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/40 to-accent/40 rounded-xl blur-sm" />
                  <div className="relative bg-card border-2 border-primary/50 rounded-xl overflow-hidden">
                    {/* Header */}
                    <div className="bg-primary/10 px-4 py-2 flex items-center justify-between border-b border-primary/20">
                      <span className="text-[10px] font-bold text-primary uppercase tracking-wider flex items-center gap-1.5">
                        <Sparkles className="h-3 w-3" /> SPONSORED • TOP PICK
                      </span>
                      <span className="text-[10px] text-accent font-medium">Best Match</span>
                    </div>
                    
                    {/* Content */}
                    <div className="p-4 space-y-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-bold text-foreground flex items-center gap-2" role="presentation">
                            Johnson's Premier Plumbing
                            <ExternalLink className="h-3 w-3 text-muted-foreground" />
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex items-center gap-0.5">
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} className="h-3 w-3 fill-accent text-accent" />
                              ))}
                            </div>
                            <span className="text-xs text-muted-foreground">4.9 (347 reviews)</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 bg-accent/10 text-accent text-[10px] px-2 py-1 rounded-full">
                          <Shield className="h-3 w-3" />
                          <span>Verified</span>
                        </div>
                      </div>

                      {/* Features Grid */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground bg-secondary/50 rounded-lg px-2.5 py-2">
                          <Clock className="h-3.5 w-3.5 text-accent" />
                          <span>24/7 Emergency</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground bg-secondary/50 rounded-lg px-2.5 py-2">
                          <MapPin className="h-3.5 w-3.5 text-accent" />
                          <span>2.3 mi away</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground bg-secondary/50 rounded-lg px-2.5 py-2">
                          <CheckCircle2 className="h-3.5 w-3.5 text-accent" />
                          <span>Licensed & Insured</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground bg-secondary/50 rounded-lg px-2.5 py-2">
                          <CheckCircle2 className="h-3.5 w-3.5 text-accent" />
                          <span>Free Estimates</span>
                        </div>
                      </div>

                      {/* CTA Buttons */}
                      <div className="flex gap-2 pt-1">
                        <button className="flex-1 bg-primary text-primary-foreground text-xs font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors">
                          <Phone className="h-3.5 w-3.5" />
                          CALL NOW
                        </button>
                        <button className="flex-1 bg-secondary border border-border text-xs font-medium py-2.5 rounded-lg flex items-center justify-center gap-2 hover:bg-secondary/80 transition-colors">
                          <MessageSquare className="h-3.5 w-3.5" />
                          MESSAGE
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Organic Results - Faded */}
                <div className="space-y-2 opacity-40">
                  <div className="bg-secondary/30 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Mike's Plumbing Co.</p>
                      <p className="text-[10px] text-muted-foreground">4.6 ★ • 156 reviews</p>
                    </div>
                    <span className="text-[10px] text-muted-foreground">3.1 mi</span>
                  </div>
                  <div className="bg-secondary/30 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">City Plumbing Services</p>
                      <p className="text-[10px] text-muted-foreground">4.4 ★ • 89 reviews</p>
                    </div>
                    <span className="text-[10px] text-muted-foreground">4.8 mi</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default AISearchMockup;
